import {
  IAuthenticationTokenResult,
  StoredAuthenticationToken
} from '@src/domain/interfaces/IAuthenticationTokenResult'
import { deleteAllCookie } from '@src/utility/Utils'

const AUTH_TOKEN_STORAGE_KEY = 'auth_tokens'
const ACCESS_TOKEN_STORAGE_KEY = 'access_token'
const REFRESH_TOKEN_BUFFER_MS = 0

const parseDate = (value?: string | null) => {
  if (!value) {
    return undefined
  }

  const timestamp = Date.parse(value)
  if (Number.isNaN(timestamp)) {
    return undefined
  }

  return timestamp
}

export const loadAuthTokens = (): StoredAuthenticationToken | null => {
  const raw = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
  if (!raw) {
    const legacyAccessToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)
    if (!legacyAccessToken) {
      return null
    }

    return {
      accessToken: legacyAccessToken,
      accessTokenExpiry: '',
      refreshToken: '',
      refreshTokenExpiry: ''
    }
  }

  try {
    const parsed = JSON.parse(raw) as IAuthenticationTokenResult
    if (!parsed || typeof parsed !== 'object') {
      return null
    }

    return {
      accessToken: parsed.accessToken,
      accessTokenExpiry: parsed.accessTokenExpiry,
      refreshToken: parsed.refreshToken,
      refreshTokenExpiry: parsed.refreshTokenExpiry
    }
  } catch (error) {
    return null
  }
}

export const persistAuthTokens = (tokens: IAuthenticationTokenResult) => {
  localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, JSON.stringify(tokens))
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, tokens.accessToken)
}

export const clearStoredAuthTokens = () => {
  localStorage.clear()
  sessionStorage.clear()
  deleteAllCookie()
}

export const performLogoutClient = () => {
  clearStoredAuthTokens()

  if (typeof window !== 'undefined') {
    const currentUrl = window.location.href || ''
    window.location.href = currentUrl ? `/login?returnUrl=${encodeURIComponent(currentUrl)}` : '/login'
  }
}

export const getStoredAccessToken = () => {
  const tokens = loadAuthTokens()
  return tokens?.accessToken
}

export const isAccessTokenValid = (tokens: StoredAuthenticationToken, bufferSeconds = 0) => {
  const expiryTimestamp = parseDate(tokens.accessTokenExpiry)
  if (!expiryTimestamp) {
    return false
  }

  const buffer = bufferSeconds * 1000
  return expiryTimestamp - buffer > Date.now()
}

export const isRefreshTokenValid = (tokens: StoredAuthenticationToken) => {
  const expiryTimestamp = parseDate(tokens.refreshTokenExpiry)
  if (!expiryTimestamp) {
    return false
  }

  return expiryTimestamp - REFRESH_TOKEN_BUFFER_MS > Date.now()
}
